const assert = require('#utils/browser-assert')
const { findControlPointsForSmoothConnections } = require('#utils/bezier-curve')
const { computeBBoxOverlap } = require('#utils/spatial-utils')
const { TransformMatrix2D } = require('#utils/transform-matrix')

const { UnboundContext } = require('./unbound-context')
const PresenceRenderer = require('./presence-renderer')
const CursorRenderer = require('./cursor-renderer')


const contextMapping = new WeakMap()
function CanvasContext2D (ctx) {
  if (contextMapping.has(ctx)) {
    return contextMapping.get(ctx)
  } else {
    contextMapping.set(ctx, this)
  }
  const dpr = ctx.canvas.ownerDocument.defaultView.devicePixelRatio || 1
  ctx.scale(dpr, dpr)
  ctx.imageSmoothingEnabled = false

  const _super = new UnboundContext()
  const subctx = createSubcontext()
  const cursorRenderer = CursorRenderer(ctx)
  const presenceRenderer = PresenceRenderer(ctx)

  const getComputedStyle = (function() {
    const $window = ctx.canvas.ownerDocument.defaultView
    let styles = null
    return function() {
      styles = styles || $window.getComputedStyle(ctx.canvas)
      return styles
    }
  }())

  Object.assign(this, _super, {
    ...cursorRenderer,
    ...presenceRenderer,
    save() {
      _super.save()
      ctx.save()
    },
    restore() {
      _super.restore()
      ctx.restore()
    },
    resetTransform() {
      _super.resetTransform()
      ctx.resetTransform()
    },
    getCanvas() {
      return ctx.canvas
    },
    getCSSVariable(name) {
      const styles = getComputedStyle()
      return styles.getPropertyValue(name)
    },
    getSubcontext() { // NOTE: only for testing
      return subctx
    },
    composite(childBBox, callback) {
      if (subctx === null) {
        return callback(this)
      }
      ctx.save()
      ctx.transform(...this.getTransform().toArray())
      const bbox = computeVisibleBBox(ctx.canvas, childBBox)
      if (bbox.width <= 0 || bbox.height <= 0) {
        ctx.restore()
        return
      }
      const subcanvas = subctx.getCanvas()
      subcanvas.width = bbox.width
      subcanvas.height = bbox.height

      subctx.save()
      // Pass our options to subcontext
      Object.entries(this.getOptions()).forEach(([name, value]) => {
        subctx.setOption(name, value)
      })
      // Offset by subcanvas position and pass our transforms to subcontext
      subcanvas.getContext('2d').setTransform(
        ...TransformMatrix2D()
          .translate(-bbox.x, -bbox.y)
          .multiply(ctx.getTransform())
          .toArray()
      )
      // Draw to subcontext
      callback(subctx)
      // Composite to primary context
      ctx.resetTransform()
      ctx.drawImage(subcanvas, bbox.x, bbox.y)
      // restore for continued rendering
      ctx.restore()
      subctx.restore()
    },
    clear() {
      this.resetTransform()
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
      ctx.scale(dpr, dpr)
    },
    drawCircle(center, radius) {
      ctx.beginPath()
      ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI)
      ctx.stroke()
      ctx.fill()
    },
    drawEllipse(center, radiusX, radiusY) {
      assert(radiusX >= 0)
      assert(radiusY >= 0)
      ctx.beginPath()
      ctx.ellipse(center.x, center.y, radiusX, radiusY, 0, 2*Math.PI, 0)
      ctx.stroke()
      ctx.fill()
    },
    drawPolygon(points) {
      assert(points.length > 2)

      ctx.beginPath()
      drawLineSegmented(ctx, points)
      ctx.closePath()
      ctx.stroke()
      ctx.fill()
    },
    drawLine(points) {
      // TODO: line-smoothing... possibly adaptive, depending on viewport zoom?
      if (points.length < 2) {
        return
      }

      ctx.beginPath()
      const opts = this.getOptions()
      if (opts.lineType === 'squiggle') {
        drawLineQuadracticCurve(ctx, points)
      } else if (opts.lineType === 'interpolated-curve') {
        drawInterpolatedCurve(ctx, points, opts.closeCurve, opts.curveTightness)
        if (opts.closeCurve) {
          ctx.closePath()
          ctx.fill()
        }
      } else {
        drawLineSegmented(ctx, points)
      }
      ctx.stroke()
    },
    drawRect({ x, y }, width, height) {
      ctx.beginPath()
      ctx.rect(x, y, width, height)
      ctx.fill()
      ctx.stroke()
    },
    drawText(start, text) {
      ctx.fillText(text, start.x, start.y)
    },
    setOptionSync(name, value) {
      if (this.getOption(name) !== value) {
        this.setOption(name, value)
        ctx[name] = value
      }
    },
    setOptionsSync(opts) {
      for (const [name, value] of Object.entries(opts)) {
        this.setOptionSync(name, value)
      }
    },
    measureText(text) {
      let measure = null
      this.withOptionsApplied(() => {
        const m1 = ctx.measureText(text)
        if (Number.isFinite(m1.fontBoundingBoxAscent)) {
          measure = {
            width: m1.width,
            left: m1.actualBoundingBoxLeft,
            right: m1.actualBoundingBoxRight,
            ascent: m1.fontBoundingBoxAscent,
            descent: m1.fontBoundingBoxDescent,
            height: m1.fontBoundingBoxAscent + m1.fontBoundingBoxDescent,
          }
        } else {
          // Fallback for missing font bounding boxes
          const m2 = ctx.measureText('AaBbCcDdEeFfGgHhIiJjKkLlMmNnOoPpQqRrSsTtUuVvWwXxYyZz')
          measure = {
            width: m1.width,
            left: m1.actualBoundingBoxLeft,
            right: m1.actualBoundingBoxRight,

            ascent: m2.actualBoundingBoxAscent,
            descent: m2.actualBoundingBoxDescent,
            height: m2.actualBoundingBoxAscent + m2.actualBoundingBoxDescent,
          }
        }
      })
      return measure
    },
    supportsFontKerning() {
      return 'fontKerning' in ctx
    },
    withOptionsApplied(callback) {
      ctx.save()
      const opts = this.getOptions()
      for (const [name, val] of Object.entries(opts)) {
        if (name === 'lineDash') {
          ctx.setLineDash(val)
        } else {
          ctx[name] = val
        }
      }
      callback()
      ctx.restore()
    },
    withTransformsApplied(callback) {
      ctx.save()
      ctx.transform(...this.getTransform().toArray())
      this.withOptionsApplied(callback)
      ctx.restore()
    }
  })

  function createSubcontext() {
    const $document = ctx.canvas.ownerDocument
    // Don't recursively create infinite subcontexts for offscreen canvases
    if (!$document.body.contains(ctx.canvas)) {
      return null
    }
    const subcanvas = $document.createElement('canvas')
    const subctx = new CanvasContext2D(subcanvas.getContext('2d'))
    return subctx
  }
}

function drawLineQuadracticCurve(ctx, points) {
  ctx.moveTo(points[0].x, points[0].y)
  // Curve through each point.
  // Use the midpoint between it and the next point for the control points
  for (let i = 1; i < points.length - 2; i++) {
    const p0 = points[i]
    const p1 = points[i + 1]
    const xc = (p0.x + p1.x) * 0.5
    const yc = (p0.y + p1.y) * 0.5
    ctx.quadraticCurveTo(p0.x, p0.y, xc, yc)
  }
  // curve through the last two points
  const p0 = points[points.length - 2]
  const p1 = points[points.length - 1]
  ctx.quadraticCurveTo(p0.x, p0.y, p1.x, p1.y)
}

function drawLineSegmented(ctx, points) {
  for (const point of points) {
    ctx.lineTo(point.x, point.y)
  }
}

function drawInterpolatedCurve(ctx, points, closed = false, t = 0.33) {
  if (points.length < 2) {
    return
  } else if (points.length === 2) {
    ctx.moveTo(points[0].x, points[0].y)
    ctx.lineTo(points[1].x, points[1].y)
  } else {
    return drawSpline(ctx, points.slice(), t, closed)
  }
}

function drawSpline(ctx, pts, t, closed) {
  const cps = findControlPointsForSmoothConnections(pts, t)
  if (closed) {
    drawClosedCurve()
  } else {
    drawOpenCurve()
  }
  return

  function drawClosedCurve() {
    ctx.moveTo(pts[0].x, pts[0].y)
    for (let i = 0; i < pts.length; i++) {
      drawBezierCurveFromIndex(i)
    }
  }

  function drawOpenCurve() {
    const n = pts.length
    // For open curves, use quadratics for first and last arcs
    drawQuadraticCurveFromTo(0, 1)
    // Cubic bezier for each inner arc
    for(let i = 1; i < n-2; i++) {
      drawBezierCurveFromIndex(i)
    }
    // Last arc is drawn backward from last point to second-last point
    drawQuadraticCurveFromTo(n-1, n-2)
  }

  function drawQuadraticCurveFromTo(i, j) {
    const controlPointIndex = i < j ? 0 : 1
    const cp = cps[j][controlPointIndex]
    const p0 = pts[i]
    const p1 = pts[j]
    ctx.moveTo(p0.x, p0.y)
    ctx.quadraticCurveTo(cp.x, cp.y, p1.x, p1.y)
  }

  function drawBezierCurveFromIndex(i) {
    const cp1 = cps[i][1]
    const cp2 = (cps[i+1] || cps[0])[0]
    const next = (i === pts.length - 1) ? pts[0] : pts[i + 1]
    ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, next.x, next.y)
  }
}

// find AABB of canvas/bbox overlap in canvas space coords
function computeVisibleBBox(canvas, bbox) {
  const matrix = TransformMatrix2D.fromMatrix(canvas.getContext('2d').getTransform())
  const tl = matrix.transformPoint({
    x: bbox.x,
    y: bbox.y
  })
  const br = matrix.transformPoint({
    x: bbox.x + bbox.width,
    y: bbox.y + bbox.height
  })
  const canvasCoordsBBox = {
    x: Math.round(tl.x),
    y: Math.round(tl.y),
    width: Math.abs(Math.round(br.x - tl.x)),
    height: Math.abs(Math.round(br.y - tl.y)),
  }
  return computeBBoxOverlap(canvasCoordsBBox, {
    x: 0,
    y: 0,
    width: canvas.width,
    height: canvas.height,
  })
}

module.exports = { CanvasContext2D }
